import { useState } from 'react';
import { Search, Upload, Users } from 'lucide-react';
import { Button } from '@/components/Button';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/States';
import { ImportContactsDialog } from '@/components/ImportContactsDialog';
import { RecipientTimelineDialog } from '@/components/RecipientTimelineDialog';
import { Pill } from '@/components/StatusPill';
import { useContacts } from '@/hooks/use-contacts';
import { formatDateTime } from '@/lib/utils';
import type { ContactStatus, ContactSummary } from '@/lib/schemas';

/**
 * The contact list.
 *
 * Composition only: the query is wired here and the dialog below renders. The search box is
 * deliberately uncontrolled by the query — typing updates local state and the query key changes,
 * which TanStack Query debounces for us through its cache rather than a hand-rolled timer.
 */
export function ContactsPage() {
  const [query, setQuery] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  // The contact whose delivery history is open. Held here rather than in the table, so the dialog is a
  // sibling of the table instead of a child of a row — a dialog rendered inside a <td> inherits the
  // table's layout and cannot be positioned.
  const [viewing, setViewing] = useState<ContactSummary | null>(null);

  const { data, isPending, isError, error, refetch } = useContacts(query, 0);

  return (
    <div className="px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-heading">Contacts</h1>
          <p className="mt-1 text-sm text-muted">
            Everyone who can receive a campaign. Shared across brands, because suppression is.
          </p>
        </div>

        <Button variant="primary" onClick={() => setIsImporting(true)}>
          <Upload className="h-4 w-4" aria-hidden="true" />
          Import CSV
        </Button>
      </div>

      <div className="mt-5">
        <div className="relative min-w-0 sm:max-w-xs">
          {/* A real label, visually hidden. A placeholder is not a label — it vanishes on focus and
              screen readers do not announce it. */}
          <label htmlFor="contact-search" className="sr-only">
            Search contacts by address or name
          </label>

          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
            aria-hidden="true"
          />

          <input
            id="contact-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="name@example.com"
            className="h-10 w-full rounded-md border border-border bg-surface pl-9 pr-3 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-border bg-surface">
        {isPending ? (
          <TableSkeleton rows={6} />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={Users}
            title={query === '' ? 'No contacts yet' : 'Nothing matched that search'}
            description={
              query === ''
                ? 'Import a CSV to get started. Every import records where the consent came from, which is the one thing that cannot be reconstructed later.'
                : 'Try a partial address or surname — the search is a substring match.'
            }
            action={
              query === '' ? (
                <Button variant="primary" onClick={() => setIsImporting(true)}>
                  <Upload className="h-4 w-4" aria-hidden="true" />
                  Import CSV
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ContactTable contacts={data.items} total={data.total} onView={setViewing} />
        )}
      </div>

      {isImporting && <ImportContactsDialog onClose={() => setIsImporting(false)} />}

      {viewing !== null && (
        <RecipientTimelineDialog
          contactId={viewing.id}
          email={viewing.email}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

/**
 * The table.
 *
 * Below ~768px it becomes a card list rather than scrolling horizontally: an address is long enough
 * that a scrolling table on a phone hides the only column that identifies the row.
 */
function ContactTable({
  contacts,
  total,
  onView,
}: {
  contacts: ContactSummary[];
  total: number;
  onView: (contact: ContactSummary) => void;
}) {
  // The legend only appears when there is something for it to explain — an explanation of two states
  // nobody on this page is in is noise.
  const hasExceptions = contacts.some((contact) => contact.status !== 'Active');

  return (
    <>
      {/* Cards on narrow screens. */}
      <ul className="divide-y divide-border md:hidden">
        {contacts.map((contact) => (
          <li key={contact.id}>
            <button
              type="button"
              onClick={() => onView(contact)}
              className="w-full px-4 py-3 text-left hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
            <p className="truncate text-sm font-medium text-heading">{contact.email}</p>
            <p className="mt-0.5 text-xs text-muted">
              {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'No name'}
              {' · '}
              {contact.consentAt === null ? 'No consent recorded' : `Consented ${formatDateTime(contact.consentAt)}`}
            </p>

            {contact.status !== 'Active' && (
              <p className="mt-1">
                <MailableStatus status={contact.status} />
              </p>
            )}
            </button>
          </li>
        ))}
      </ul>

      <table className="hidden w-full text-left text-sm md:table">
        <thead className="border-b border-border bg-surface-2 text-xs uppercase tracking-wide text-muted">
          <tr>
            <th scope="col" className="px-4 py-2 font-medium">Address</th>
            <th scope="col" className="px-4 py-2 font-medium">Name</th>
            <th scope="col" className="px-4 py-2 font-medium">Status</th>
            <th scope="col" className="px-4 py-2 font-medium">Consent</th>
            <th scope="col" className="px-4 py-2 font-medium">Last opened</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-border">
          {contacts.map((contact) => (
            <tr key={contact.id} className="hover:bg-surface-2">
              <td className="max-w-xs px-4 py-2">
                {/* A real button, not an onClick on the <tr>: a clickable row has no role, no keyboard
                    access and no focus ring. The address is the label because it is what identifies
                    the row. */}
                <button
                  type="button"
                  onClick={() => onView(contact)}
                  className="block max-w-full truncate text-left font-medium text-heading underline decoration-transparent hover:decoration-inherit focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {contact.email}
                </button>
              </td>
              <td className="px-4 py-2 text-muted">
                {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—'}
              </td>
              <td className="px-4 py-2 text-muted">
                <MailableStatus status={contact.status} />
              </td>
              <td className="px-4 py-2 text-muted">
                {contact.consentAt === null ? (
                  // Not colour alone: the words carry the meaning for anyone who cannot see the tint.
                  <span className="rounded bg-warning-bg px-1.5 py-0.5 text-xs text-warning-ink">
                    Not recorded
                  </span>
                ) : (
                  formatDateTime(contact.consentAt)
                )}
              </td>
              <td className="px-4 py-2 text-muted">
                {contact.lastOpenedAt === null ? '—' : formatDateTime(contact.lastOpenedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="border-t border-border px-4 py-2 text-xs text-muted">
        Choose an address to see what happened to the mail we sent them.{' · '}
        Showing {contacts.length} of {total.toLocaleString()}
        {hasExceptions && (
          <>
            {' · '}
            <strong className="font-medium">Unconfirmed</strong> means a double opt-in is still
            outstanding, so they are excluded from every send.{' '}
            <strong className="font-medium">Erased</strong> means the person asked to be removed and
            only a suppression record remains.
          </>
        )}
      </p>
    </>
  );
}

/**
 * Whether this person will actually receive anything.
 *
 * Only the exceptions get a chip; `Active` stays as quiet text. That matches the consent cell beside
 * it and, more importantly, means the two rows on the page that will NOT be mailed are the two that
 * draw the eye — which is the question somebody scanning this list is asking.
 *
 * Worth having at all because the list is otherwise indistinguishable: an unconfirmed contact looks
 * like a normal one, is silently excluded from every send, and the author has no way to know why the
 * audience count is lower than the row count.
 */
function MailableStatus({ status }: { status: ContactStatus }) {
  if (status === 'Active') return <span className="text-xs text-muted">Active</span>;

  if (status === 'PendingConfirmation') {
    return (
      <Pill tone="warning">Unconfirmed</Pill>
    );
  }

  return <Pill tone="neutral">Erased</Pill>;
}
